import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import StarRating from './StarRating';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  padding: 2rem 0;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }
`;

const ScoreBlock = styled.div`
  text-align: center;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    text-align: left;
  }
`;

const StarsRow = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2px;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    justify-content: flex-start;
  }
`;

const BigNum = styled.div`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2.65rem, 4.5vw + 1.5rem, 3.75rem);
  font-weight: 300;
  margin-bottom: 0.35rem;
  color: ${({ theme }) => theme.colors.foreground};
`;

const CountLine = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.type.bodySm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-family: ${({ theme }) => theme.fonts.sans};
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const WriteBtn = styled.button`
  margin-top: 1rem;
  padding: 0.65rem 1.25rem;
  font-size: ${({ theme }) => theme.type.bodyXs};
  font-family: ${({ theme }) => theme.fonts.sans};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border: 1px solid ${({ theme }) => theme.colors.foreground};
  background: transparent;
  color: ${({ theme }) => theme.colors.foreground};
  cursor: pointer;
  transition:
    background 0.2s ease,
    color 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.foreground};
    color: ${({ theme }) => theme.colors.cream};
  }
`;

const Bars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: ${({ theme }) => theme.type.bodyXs};
  font-family: ${({ theme }) => theme.fonts.sans};
`;

const StarsLabel = styled.span`
  width: 1.5rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const Track = styled.div`
  flex: 1;
  height: 8px;
  background: ${({ theme }) => theme.colors.secondary};
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.colors.foreground};
  width: ${({ $pct }) => $pct}%;
`;

const Num = styled.span`
  width: 2rem;
  text-align: right;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const RecCol = styled.div`
  text-align: center;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    text-align: left;
  }
`;

const RecPct = styled.div`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.75rem, 2vw + 1rem, 2rem);
  font-weight: 300;
`;

const Disclaimer = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.type.bodyXs};
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-family: ${({ theme }) => theme.fonts.sans};
  line-height: 1.6;
`;

const emptyDist = () => ({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });

export default function ReviewSummary({ summary, onWriteReview, liveFromApi = false }) {
  const { t } = useTranslation();
  const count = summary?.count ?? 0;
  const avg = count ? summary?.average ?? 0 : 0;
  const dist = { ...emptyDist(), ...(summary?.distribution || {}) };
  const recPct =
    summary?.recommendPercent != null
      ? summary.recommendPercent
      : count && dist[5] + dist[4] > 0
        ? Math.round(((dist[5] + dist[4]) / count) * 100)
        : 0;

  return (
    <Grid>
      <ScoreBlock>
        <BigNum>{avg.toFixed(1)}</BigNum>
        <StarsRow>
          <StarRating rating={avg} size="lg" />
        </StarsRow>
        <CountLine style={{ marginTop: '0.35rem' }}>{t('modamai.reviews.snapshot', { count })}</CountLine>
        {onWriteReview ? (
          <WriteBtn type="button" onClick={onWriteReview}>
            {t('modamai.reviews.writeReview')}
          </WriteBtn>
        ) : null}
      </ScoreBlock>

      <Bars>
        {[5, 4, 3, 2, 1].map((stars) => {
          const c = dist[stars] ?? 0;
          const pct = count ? (c / count) * 100 : 0;
          return (
            <BarRow key={stars}>
              <StarsLabel>{stars}★</StarsLabel>
              <Track>
                <Fill $pct={pct} />
              </Track>
              <Num>{c}</Num>
            </BarRow>
          );
        })}
      </Bars>

      <RecCol>
        <RecPct>{recPct}%</RecPct>
        <CountLine>{t('modamai.reviews.wouldRecommend')}</CountLine>
        <Disclaimer style={{ marginTop: '1rem' }}>
          {liveFromApi ? t('modamai.reviews.disclaimerLive') : t('modamai.reviews.disclaimerOffline')}
        </Disclaimer>
      </RecCol>
    </Grid>
  );
}
